/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import {
  EMAIL_CHECK_API,
  SEND_OTP_API,
  UPDATE_WITH_OTP_API,
  RESET_PASSWORD_API,
  SIGN_UP_PROXY_API,
  LOGIN_PROXY_API
} from './../../../constants';
import { parseUrl } from 'query-string';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { getPath } from '../../../utils';
import { useEffect } from "react";

export const emailValidation = targetValue => {
  const email = RegExp(/[A-Za-z@]/);
  return email.test(targetValue);
};

export const triggerSendOtp = async ({ phoneNumber = '', email = '', countryIso = '', sketchesHost }) => {
  let sendOtpBody = phoneNumber
    ? { 'phone-number': phoneNumber, country: countryIso, 'always-send': true }
    : { email: email, 'always-send': true };
  const url = getPath({ sketchesHost, relativePath: SEND_OTP_API });
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sendOtpBody)
  });
  return response?.status;
};

export const triggerEmailCheck = async ({ email = '', sketchesHost }) => {
  const url = getPath({ sketchesHost, relativePath: `${EMAIL_CHECK_API}${encodeURIComponent(email)}` });
  const response = await (
    await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
  ).json();
  return response?.exists;
};

export const getHeaderCookies = (headers = {}) => {
  const headersArray = Array.from(headers.entries());
  const xQtAuthArray = headersArray.find(([key, value]) => key.toLowerCase().includes('qt-auth'));
  const qtAuthString = xQtAuthArray?.find(element => element.includes('qt-auth='));
  const qtAuthValue = qtAuthString ? qtAuthString.split('=')[1].split(';')[0] : null;
  return qtAuthValue;
};

export const triggerLogin = async ({ type = '', data = '', credential = '', sketchesHost }) => {
  let loginBody;
  if (type === 'otp') {
    loginBody = { 'phone-number': data, otp: credential, 'auto-signup': true };
  } else {
    loginBody = { username: data, password: credential, 'auto-signup': false, email: data };
  }
  const url = getPath({ sketchesHost, relativePath: LOGIN_PROXY_API });
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(loginBody)
  });
  const { data: responseData = {}, headers = {} } = await response.json();
  const verificationStatus = get(responseData, ['user', 'verification-status'], '');
  let qtAuthCookie;
  if (!verificationStatus) {
    const cookieArray = headers?.['set-cookie']?.find(element => element.includes('qt-auth='));
    qtAuthCookie = cookieArray ? cookieArray.split('=')[1].split(';')[0] : null;
  }
  return { responseStatus: response?.status, qtAuthCookie: qtAuthCookie, verificationStatus: verificationStatus };
};

export const triggerSignUp = async ({ email = '', name = '', password = '', sketchesHost }) => {
  const signUpBody = {
    email: email,
    name: name,
    username: email,
    password: password
  };
  const url = getPath({ sketchesHost, relativePath: SIGN_UP_PROXY_API });
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(signUpBody)
  });
  const qtAuthCookie = getHeaderCookies(response?.headers);
  return { responseStatus: response?.status, qtAuthCookie: qtAuthCookie };
};

export const triggerUpdateWithOtp = async ({ otp = '', email = '', authCookie = '', sketchesHost }) => {
  const updateWithOtpBody = {
    otp: otp,
    user: {
      'verification-status': 'email',
      email: email
    }
  };
  const url = getPath({ sketchesHost, relativePath: UPDATE_WITH_OTP_API });
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-QT-AUTH': authCookie
    },
    body: JSON.stringify(updateWithOtpBody)
  });
  return response?.status;
};

export const triggerResetPassword = async ({ email = '', otp = '', password = '', sketchesHost }) => {
  const resetPasswordBody = {
    email: email,
    otp: otp,
    'new-password': password
  };
  const url = getPath({ sketchesHost, relativePath: RESET_PASSWORD_API });
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(resetPasswordBody)
  });
  return response?.status;
};

export const genericError = (response = '', api = '') => {
  const genericErrorStatusCodes = {
    'send-otp-email': [500, 429, 401, 404],
    'send-otp-mobile': [500, 429, 401, 404],
    'login-email': [500],
    'login-mobile': [500],
    'sign-up': [500],
    'reset-password': [404, 500],
    'update-with-otp': [500, 400, 409, 422]
  };
  return get(genericErrorStatusCodes, [api], []).includes(response) || null;
};

export const inputFieldError = (response = '', api = '') => {
  const genericErrorStatusCodes = {
    'send-otp-email': [400],
    'send-otp-mobile': [400],
    'login-email': [401],
    'login-mobile': [401],
    'sign-up': [400, 401, 409],
    'reset-password': [401],
    'update-with-otp': [401],
    'email-check': [200]
  };

  return get(genericErrorStatusCodes, [api], []).includes(response) || null;
};

export const getErrorStyle = (response, api, mode) => {
  return inputFieldError(response, api) ? `error-outline ${mode}` : '';
};

export const getDarkModeStyle = () => {
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  return enableDarkMode ? 'dark' : 'light';
};

export const triggerSSO = async props => {
  const { sso, currentPath = '', currentHostUrl = '', sketchesHost } = props;
  const { oauthAuthorize } = await import('@quintype/bridgekeeper-js');
  const params = parseUrl(currentPath);
  const callbackUrl = get(params, ['query', 'callback_uri'], currentHostUrl);
  const redirectUrl = get(
    params,
    ['query', 'redirect_uri'],
    getPath({
      sketchesHost,
      relativePath: '/api/auth/v1/oauth/token',
      absolutePath: `${window?.location?.origin}/api/auth/v1/oauth/token`
    })
  );
  const allowAjax = true;
  const oauthResponse = await oauthAuthorize(sso?.bridgeKeeperClientId, redirectUrl, callbackUrl, allowAjax);
  if (oauthResponse?.redirect_uri) window.location.href = oauthResponse.redirect_uri;
};

export const retrieveNumbers = value => value.replace(/[^0-9]/g, '');

export const handleScrollToTop = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
};
